import React, { useEffect, useRef } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { ScrollView } from '../../ui';
import { Page, ScrollToTop, LayoutBox } from '../../components';
import { navLabelFor } from '../../components/AppShell/navItems';

/* The dashboard's two scroll regions, moved unchanged out of Dashboard.jsx (UND-1133, Shell layout).
   They are infrastructure, not visual components: the legacy ListRenderer lists outside the change
   (Bind Forms, Admin Locations, the ApplicationView quotes tab, the Aggregation quote picker, Submit
   Claim's PolicyList) read their scroll node and scroll events from these ScrollViews, and their
   translateZ(0) keeps position:fixed overlays inside the content area. The list region shows for the
   dashboard's list paths and the detail region for everything else, toggled by display.

   listRegionScrolls is false on /policies, /quotes and /applications, whose shadcn tables are their
   own scrollers: then the list region does not scroll, it is left unnamed so the table's own region
   is the one named for the list, and its height is bounded all the way down to the table. The
   ScrollView's content container gets the region's height (a ScrollView's content otherwise grows
   with what it holds) and the Page fills it instead of 101vh. Without that bound the table never
   overflows: it can't scroll, and filling the list loads page after page. detailRegionScrolls is the
   same for the detail region, false on Admin › Portal Users, whose list is the same shadcn table. */

export const dashboardPaths = ['/applications', '/quotes', '/bind-forms', '/policies', '/analytics', '/aggregation', '/aggregation/pml-snapshot'];

const useDisplayForPaths = (paths = [], inverse) => {
  const location = useLocation();
  const pathname = location ? location.pathname : '';
  const includes = paths.includes(pathname);
  let display = includes ? true : false;
  if (inverse) {
    display = !display;
  }
  return display ? 'flex' : 'none';
};

const ScrollContainer = ({ displayForPaths, inverse = false, named = false, scrolls = true, children }) => {
  const display = useDisplayForPaths(displayForPaths, inverse);
  const scrollNode = useRef();
  const tabMatch = useRouteMatch('/:tab');
  const regionLabel = named && scrolls && tabMatch ? navLabelFor(tabMatch.params.tab) : undefined;
  useEffect(() => {
    if (scrollNode.current) {
      if (display !== 'flex') {
        if (!scrollNode.current.scrollEventManager.eventsDisabled) {
          scrollNode.current.scrollEventManager.disableEvents();
        }
      } else if (scrollNode.current.scrollEventManager.eventsDisabled) {
        scrollNode.current.scrollEventManager.enableEvents();
      }
    }
  }, [display]);
  return (
    <ScrollView
      ref={scrollNode}
      width="100%"
      maxWidth="100%"
      height="100%"
      maxHeight="100vh"
      display={display}
      scrollEnabled={display === 'flex' && scrolls}
      accessibilityRole={regionLabel ? 'region' : undefined}
      accessibilityLabel={regionLabel}
      containerStyle={scrolls ? undefined : { height: '100%' }}
      provideNode
      contentContainer={{
        width: '100%',
        maxWidth: '100%',
        flex: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      <Page {...(scrolls ? { minHeight: '101vh' } : { height: '100%' })} row="md" bg="$primary">
        <ScrollToTop
          trigger={(curr, last) => {
            if (curr !== last) {
              const startsWithDashboardPath = dashboardPaths.some((path) => curr.startsWith(path));
              if (startsWithDashboardPath) {
                return curr;
              }
            }
            return last;
          }}
        />
        <LayoutBox flex={1} width="100%" bg="$white">
          {children}
        </LayoutBox>
      </Page>
    </ScrollView>
  );
};

export const DashboardScrollRegions = ({ list, listRegionScrolls = true, detailRegionScrolls = true, children }) => {
  return (
    <>
      <ScrollContainer displayForPaths={dashboardPaths} named scrolls={listRegionScrolls}>
        {list}
      </ScrollContainer>
      <ScrollContainer displayForPaths={dashboardPaths} inverse scrolls={detailRegionScrolls}>
        {children}
      </ScrollContainer>
    </>
  );
};
